/**
 * Benefit definitions for Long-Term Disability (LTD)
 *
 * Uses builder functions for standard patterns.
 * Enum values provided via typeEnumValues parameter using OpenAPI types.
 */

import {
  addNotDisclosed,
  addNotDisclosedAndNotOffered,
  BaseBenefitPlanPolicyPeriod,
  BenefitAmountTypeEnum,
  BenefitDurationTypeEnum,
  DefinitionOfDisabilityEnum,
  DefinitionOfEarningsEnum,
  EliminationPeriodTypeEnum,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  MaximumAmountTypeEnum,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpGainfulEarningsTestType,
  RfpInclusionStatusEnum,
  RfpLimitationType,
  RfpLtdOtherLimitationsEnum,
  RfpOwnOccupationPeriodType,
  RfpRehabEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildCommissionBenefits,
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
} from "../shared/definitions/builders";
import {
  benefitDurationLabels,
  definitionOfDisabilityLabels,
  definitionOfEarningsLabels,
  employerContributionLabels,
} from "../shared/definitions/customLabels";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

// Shared description for timeframe-based limitations
const timeframeLimitationDescription =
  "Timeframe amount (years or months based on type)";

export const ltdBenefitDefinitions: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description:
      "Current carrier name (disabled if policy period = new or alternate)",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  // Extended (non-v5) fields (CD-1940/CD-2067). Backend fields are plain
  // strings for now (RFC-004), not yet enums, so all fields are text,
  // except eligibilityHours (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  benefitAmountType: {
    label: "Benefit Amount",
    description: "Type of benefit amount calculation",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(BenefitAmountTypeEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
    refreshesCells: ["benefitAmountAmount", "benefitAmountMinimum"],
  },
  benefitAmountAmount: {
    label: "Amount",
    description: "Benefit amount value (dollar or percentage based on type)",
    cell: sharedCellSpecs.dollarAmount, // Default cell spec
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitAmountType",
      disabledValues: [NotDisclosed],
    },
    cellVariants: {
      dependsOn: "benefitAmountType",
      options: {
        increments: sharedCellSpecs.dollarAmount,
        percentageOfSalary: sharedCellSpecs.percentageAmount,
      },
      default: sharedCellSpecs.dollarAmount,
    },
  },
  benefitAmountMinimum: {
    label: "Minimum",
    description: "Minimum benefit amount",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitAmountType",
      disabledValues: addNotDisclosed([
        BenefitAmountTypeEnum.percentageOfSalary,
      ]),
      resetValue: null,
    },
  },
  // Minimum Amount is a type/amount pair. valueSuffix is "" rather than the
  // default "Amount" because the field is already named "Minimum Amount"; the
  // amount half keeps the bare "minimumAmount" key (CD-2325 sibling work).
  ...buildTypeAmountBenefitPair({
    prefix: "minimumAmount",
    valueSuffix: "",
    typeLabel: "Minimum Amount",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: sharedCellSpecs.minimumBenefitTypeValues,
    typeCustomLabels: sharedCellSpecs.minimumBenefitTypeLabels,
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of minimum benefit amount structure",
    amountDescription: "Minimum benefit amount",
    disabledValues: ["notDisclosed"],
    mpConfigKey: "minimum_amount",
    mpOnly: true,
    amountCellVariants: {
      options: { percentageOfSalary: sharedCellSpecs.percentageAmount },
      default: sharedCellSpecs.dollarAmount,
    },
  }),
  maximumAmountType: {
    label: "Maximum Amount",
    description: "Type of maximum benefit amount",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(MaximumAmountTypeEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
    refreshesCells: ["maximumAmountDollarAmount", "maximumAmountPercent"],
  },
  maximumAmountDollarAmount: {
    label: "Dollar",
    description: "Maximum amount in dollars",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "maximumAmountType",
      disabledValues: ["", "unlimited", NotDisclosed],
    },
  },
  maximumAmountPercent: {
    label: "Percentage",
    description: "Maximum amount as percentage",
    cell: sharedCellSpecs.percentageAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "maximumAmountType",
      disabledValues: ["", "dollarAmount", "unlimited", NotDisclosed],
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "guaranteedIssue",
    typeLabel: "Guaranteed Issue",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: addNotDisclosedAndNotOffered([
      "dollarAmount",
      "notIncluded",
    ]),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of guaranteed issue structure",
    amountDescription: "Guaranteed issue amount",
    amountDefaultValue: 0,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "ownOccupationPeriod",
    typeLabel: "Own Occupation Period",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpOwnOccupationPeriodType,
    ),
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of own occupation period",
    amountDescription: "Own occupation period amount (years or months)",
    amountDefaultValue: 1,
    disabledValues: [
      "ownOccupationOnly",
      "notIncluded",
      NotDisclosed,
      NotOffered,
    ],
    amountCellVariants: {
      options: {
        yearsOwnOccupation: { ...sharedCellSpecs.numberField, suffix: "years" },
        monthsOwnOccupation: {
          ...sharedCellSpecs.numberField,
          suffix: "months",
        },
      },
      default: sharedCellSpecs.numberField,
    },
  }),
  definitionOfDisability: {
    label: "Definition of Disability",
    description: "How disability is defined for eligibility",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(DefinitionOfDisabilityEnum),
        formatValue: createEnumFormatter(definitionOfDisabilityLabels),
      },
      valueFormatter: createValueFormatter(definitionOfDisabilityLabels),
    },
    networkDependent: false,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "gainfulEarningsTestOwnOccupation",
    mpConfigKey: "gainful_earnings_test_own_occupation",
    typeLabel: "Gainful Earnings Test - Own Occupation",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpGainfulEarningsTestType,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of gainful earnings test for own occupation",
    amountDescription: "Percentage for gainful earnings test",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "gainfulEarningsTestAnyOccupation",
    mpConfigKey: "gainful_earnings_test_any_occupation",
    typeLabel: "Gainful Earnings Test - Any Occupation",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpGainfulEarningsTestType,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of gainful earnings test for any occupation",
    amountDescription: "Percentage for gainful earnings test",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "eliminationPeriod",
    typeLabel: "Elimination Period",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      EliminationPeriodTypeEnum,
    ),
    amountLabel: "Number of Days",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of waiting period",
    amountDescription: "Number of days for elimination period",
    valueSuffix: "Days",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "benefitDuration",
    typeLabel: "Benefit Duration",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(BenefitDurationTypeEnum),
    typeCustomLabels: benefitDurationLabels,
    amountLabel: "Amount",
    amountCell: { ...sharedCellSpecs.numberField, suffix: "years" },
    typeDescription: "Type of benefit duration",
    amountDescription: "Duration amount (when applicable for type)",
    amountDefaultValue: 1,
    disabledValues: [
      ...Object.values(BenefitDurationTypeEnum).filter(
        (v) =>
          v !== BenefitDurationTypeEnum.lesserOfRbdOrNumberOfYears &&
          v !== BenefitDurationTypeEnum.yearDuration &&
          v !== BenefitDurationTypeEnum.laterOfAgeOrSsnra,
      ),
      NotDisclosed,
    ],
    isRequired: true,
  }),
  zeroDayResidual: {
    mpOnly: true,
    label: "Zero Day Residual",
    mpConfigKey: "zero_day_residual",
    description: "Zero day residual disability benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  indexingOfPreDisabilityEarnings: {
    mpOnly: true,
    label: "Indexing of Pre-Disability Earnings",
    mpConfigKey: "indexing_of_pre_disability_earnings",
    description: "Indexing method applied to pre-disability earnings",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  returnToWorkIncentivePeriod: {
    mpOnly: true,
    label: "Return to Work Incentive Period",
    mpConfigKey: "return_to_work_incentive_period",
    description: "Return to work incentive period details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "definitionOfEarnings",
    mpConfigKey: "definition_of_earnings",
    typeLabel: "Definition of Earnings",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(DefinitionOfEarningsEnum),
    typeCustomLabels: definitionOfEarningsLabels,
    amountLabel: "Average Month",
    amountCell: { ...sharedCellSpecs.numberField, suffix: "months" },
    typeDescription: "What earnings are considered for benefit calculation",
    amountDescription: "Number of months to average earnings",
    // Blank, not 1: average months is optional, and 0 is a real value.
    amountDefaultValue: null,
    valueSuffix: "AverageMonths",
    disabledValues: [
      ...Object.values(DefinitionOfEarningsEnum).filter(
        (v) =>
          v !== "salaryPlusCommissions" &&
          v !== "salaryPlusBonus" &&
          v !== "salaryPlusBonusPlusCommissions",
      ),
      NotDisclosed,
    ],
  }),
  definitionOfPartialDisability: {
    mpOnly: true,
    label: "Definition of Partial Disability",
    mpConfigKey: "definition_of_partial_disability",
    description: "How partial disability is defined for eligibility",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  otherIncomeIntegration: {
    mpOnly: true,
    label: "Other Income Integration",
    mpConfigKey: "other_income_integration",
    description: "Integration with other income sources",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  socialSecurityIntegration: {
    mpOnly: true,
    label: "Social Security Integration",
    mpConfigKey: "social_security_integration",
    description: "Integration with Social Security benefits",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  trialWorkDays: {
    mpOnly: true,
    label: "Trial Work Days",
    mpConfigKey: "trial_work_days",
    description: "Trial work days provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  survivorBenefit: {
    mpOnly: true,
    label: "Survivor Benefit",
    mpConfigKey: "survivor_benefit",
    description: "Survivor benefit provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  worksiteModification: {
    mpOnly: true,
    label: "Worksite Modification",
    mpConfigKey: "worksite_modification",
    description: "Worksite modification benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  dayCareBenefits: {
    mpOnly: true,
    label: "Day Care Benefits",
    mpConfigKey: "day_care_benefits",
    description: "Day care benefits provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  reasonableAccommodation: {
    mpOnly: true,
    label: "Reasonable Accommodation",
    mpConfigKey: "reasonable_accommodation",
    description: "Reasonable accommodation provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  rehabilitativeIncentiveBenefit: {
    mpOnly: true,
    label: "Rehabilitative Incentive Benefit",
    mpConfigKey: "rehabilitative_incentive_benefit",
    description: "Rehabilitative incentive benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  prudentPerson: {
    mpOnly: true,
    label: "Prudent Person",
    mpConfigKey: "prudent_person",
    description: "Prudent person provision details",
    cell: sharedCellSpecs.prudentPersonDropdown,
    networkDependent: false,
  },
  selfReportedLimitations: {
    mpOnly: true,
    label: "Self-Reported Limitations",
    mpConfigKey: "self_reported_limitations",
    description: "Self-reported limitations provision details",
    cell: sharedCellSpecs.selfReportedLimitationsDropdown,
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "otherLimitations",
    mpConfigKey: "special_conditions_limitations",
    typeLabel: "Special Conditions Limitations",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpLtdOtherLimitationsEnum,
    ),
    amountLabel: "Timeframe",
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Indicates if additional plan limitations exist in the quoted plan such as special conditions, self-reported limitations, etc.",
    amountDescription: "Timeframe amount (years or months based on type)",
    amountDefaultValue: 1,
    disabledValues: [
      "noLimit",
      "payableSameAsAnyOtherDisability",
      "included",
      "notIncluded",
      NotDisclosed,
      NotOffered,
    ],
    amountCellVariants: {
      options: {
        years: { ...sharedCellSpecs.numberField, suffix: "years" },
        months: { ...sharedCellSpecs.numberField, suffix: "months" },
      },
      default: sharedCellSpecs.numberField,
    },
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "mentalIllness",
    mpConfigKey: "mental_illness",
    typeLabel: "Mental Illness",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(RfpLimitationType),
    amountLabel: "Timeframe",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Time period limitation for mental illness",
    amountDescription: timeframeLimitationDescription,
    amountDefaultValue: 1,
    disabledValues: [
      ...Object.values(RfpLimitationType).filter(
        (v) => v !== "years" && v !== "months",
      ),
      NotDisclosed,
      NotOffered,
    ],
    amountCellVariants: {
      options: {
        years: { ...sharedCellSpecs.numberField, suffix: "years" },
        months: { ...sharedCellSpecs.numberField, suffix: "months" },
      },
      default: sharedCellSpecs.numberField,
    },
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "substanceAbuse",
    mpConfigKey: "substance_abuse",
    typeLabel: "Substance Abuse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(RfpLimitationType),
    amountLabel: "Timeframe",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Time period limitation for substance abuse",
    amountDescription: timeframeLimitationDescription,
    amountDefaultValue: 1,
    disabledValues: [
      ...Object.values(RfpLimitationType).filter(
        (v) => v !== "years" && v !== "months",
      ),
      NotDisclosed,
      NotOffered,
    ],
    amountCellVariants: {
      options: {
        years: { ...sharedCellSpecs.numberField, suffix: "years" },
        months: { ...sharedCellSpecs.numberField, suffix: "months" },
      },
      default: sharedCellSpecs.numberField,
    },
  }),
  ...buildPreExistingConditionBenefitPair({
    typeDescription: "Type of pre-existing condition limitation",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed, NotOffered],
    monthsDetailDefaultValue: "01/01",
    includeNotOffered: true,
  }),
  recoveryBenefit: {
    mpOnly: true,
    label: "Recovery Benefit",
    mpConfigKey: "recovery_benefit",
    description: "Recovery benefit provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  // Unlike its sibling extended fields, mandatoryRehabilitation has a
  // confirmed dropdown value list (CD-2419/CD-2553).
  mandatoryRehabilitation: {
    mpOnly: true,
    label: "Mandatory Rehabilitation",
    mpConfigKey: "mandatory_rehabilitation",
    description: "Mandatory rehabilitation requirement details",
    cell: sharedCellSpecs.mandatoryRehabilitationDropdown,
    networkDependent: false,
  },
  residencyRestriction: {
    mpOnly: true,
    label: "Residency Restriction",
    mpConfigKey: "residency_restriction",
    description: "Residency restriction provision details",
    cell: sharedCellSpecs.residencyRestrictionDropdown,
    networkDependent: false,
  },
  progressiveIllness: {
    mpOnly: true,
    label: "Progressive Illness",
    mpConfigKey: "progressive_illness",
    description: "Progressive illness provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  infectiousContagiousDisease: {
    mpOnly: true,
    label: "Infectious & Contagious Disease",
    mpConfigKey: "infectious_contagious_disease",
    description: "Infectious and contagious disease provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  specificLossAdDAndLossOfSight: {
    mpOnly: true,
    label: "Specific Loss (AD&D and Loss of Sight)",
    mpConfigKey: "specific_loss_ad_d_and_loss_of_sight",
    description: "Specific loss (AD&D and loss of sight) benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  activitiesOfDailyLiving: {
    mpOnly: true,
    label: "Activities of Daily Living",
    mpConfigKey: "activities_of_daily_living",
    description: "Activities of daily living provision details",
    cell: sharedCellSpecs.activitiesOfDailyLivingDropdown,
    networkDependent: false,
  },
  maximumCapacity: {
    mpOnly: true,
    label: "Maximum Capacity",
    mpConfigKey: "maximum_capacity",
    description: "Maximum capacity provision details",
    cell: sharedCellSpecs.maximumCapacityDropdown,
    networkDependent: false,
  },
  rehab: {
    label: "Rehab (mandatory or enhanced)",
    mpConfigKey: "rehab_mandatory_or_enhanced",
    description: "Rehabilitation requirement to be eligible for benefit",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpRehabEnum),
      },
    },
    networkDependent: false,
  },
  retirementIncomeProtection: {
    mpOnly: true,
    label: "Retirement Income Protection",
    mpConfigKey: "retirement_income_protection",
    description: "Retirement income protection provision details",
    cell: sharedCellSpecs.retirementIncomeProtectionDropdown,
    networkDependent: false,
  },
  recurrentDisability: {
    mpOnly: true,
    label: "Recurrent Disability",
    mpConfigKey: "recurrent_disability",
    description: "Recurrent disability provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  cola: {
    label: "COLA",
    mpConfigKey: "cola_included",
    description: "Cost of Living Adjustment inclusion",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  conversion: {
    label: "Conversion",
    mpConfigKey: "conversion",
    description: "Policy conversion privilege",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  waiverOfPremium: {
    label: "Waiver of Premium",
    mpConfigKey: "waiver_of_premium",
    description: "Premium waiver in case of critical illness or serious injury",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  portability: {
    mpOnly: true,
    label: "Portability",
    mpConfigKey: "portability",
    description: "Portability provision details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  eap: {
    mpOnly: true,
    label: "EAP",
    mpConfigKey: "eap",
    description: "Employee assistance program details",
    cell: sharedCellSpecs.eapDropdown,
    networkDependent: false,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Defines employer vs employee contribution structure",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    networkDependent: false,
    isRequired: true,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
    requestedPlanRowVisibility: {
      field: "employerContribution",
      equals: EmployerContributionEnum.voluntary,
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),
} as const;
